import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const AuthContext = createContext(null);

const ACCESS_TOKEN_KEY  = 'radius_access_token';
const REFRESH_TOKEN_KEY = 'radius_refresh_token';
const USER_KEY          = 'radius_user';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:9292/api/v1';

export function AuthProvider({ children }) {
  const [user, setUser]               = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY)); } catch { return null; }
  });
  const [accessToken, setAccessToken] = useState(() => localStorage.getItem(ACCESS_TOKEN_KEY));
  const [loading, setLoading]         = useState(false);
  const refreshTimerRef               = useRef(null);

  /* ── persist helpers ── */
  const saveSession = useCallback((data) => {
    localStorage.setItem(ACCESS_TOKEN_KEY,  data.access_token);
    localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token || localStorage.getItem(REFRESH_TOKEN_KEY) || '');
    if (data.admin_user) localStorage.setItem(USER_KEY, JSON.stringify(data.admin_user));
    setAccessToken(data.access_token);
    if (data.admin_user) setUser(data.admin_user);
  }, []);

  const clearSession = useCallback(() => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setAccessToken(null);
    setUser(null);
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
  }, []);

  /* ── auto-refresh: schedule 60s before expiry ── */
  const scheduleRefresh = useCallback((expiresIn) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    const delay = Math.max((expiresIn - 60) * 1000, 5000);
    refreshTimerRef.current = setTimeout(async () => {
      const rt = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!rt) return clearSession();
      try {
        const res  = await fetch(`${BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: rt }),
        });
        if (!res.ok) return clearSession();
        const data = await res.json();
        localStorage.setItem(ACCESS_TOKEN_KEY, data.access_token);
        setAccessToken(data.access_token);
        scheduleRefresh(data.expires_in || 3600);
      } catch {
        clearSession();
      }
    }, delay);
  }, [clearSession]);

  /* ── on mount: if we have a token already, schedule refresh ── */
  useEffect(() => {
    const at = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (at) scheduleRefresh(3600);
    return () => { if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current); };
  }, [scheduleRefresh]);

  /* ── login ── */
  const login = useCallback(async (username, password) => {
    setLoading(true);
    try {
      const res  = await fetch(`${BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        // 428 = setup_required
        if (res.status === 428 && data.code === 'setup_required') {
          return { needsSetup: true };
        }
        throw new Error(data.error || 'Login gagal');
      }
      saveSession(data);
      scheduleRefresh(data.expires_in || 3600);
      return { ok: true };
    } finally {
      setLoading(false);
    }
  }, [saveSession, scheduleRefresh]);

  /* ── setup ── */
  const setup = useCallback(async ({ username, password, email, fullname }) => {
    setLoading(true);
    try {
      const res  = await fetch(`${BASE_URL}/auth/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, email, fullname }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Setup gagal');
      return { ok: true };
    } finally {
      setLoading(false);
    }
  }, []);

  /* ── logout ── */
  const logout = useCallback(() => clearSession(), [clearSession]);

  /* ── change password ── */
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    const res  = await fetch(`${BASE_URL}/auth/me`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem(ACCESS_TOKEN_KEY)}`,
      },
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Gagal ganti password');
    return data;
  }, []);

  return (
    <AuthContext.Provider value={{ user, accessToken, loading, login, setup, logout, changePassword, isAuthenticated: !!accessToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }
